import { sleep } from '@pulse/common';
import { PulseEnvironment } from '../globals';

interface DebugControls {
    container: HTMLDivElement;
    fragment: DocumentFragment;
    button: HTMLButtonElement;
}

type ControlName = 'experiment' | 'debug';

interface DebugControlInfo {
    controlName: ControlName;
    content: string;
    url: string;
    deviceId?: string;
    title: string;
    callbackFn: () => void;
}

export class DebugControlsService {
    private registeredConrols: Map<string, DebugControls | null> = new Map();
    private debugCtrlIconKey = 'pulse-debug-ctrl-icon';
    private experimentIcon = `<svg class="${this.debugCtrlIconKey}"  width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 2C9 1.44772 9.44772 1 10 1H14C14.5523 1 15 1.44772 15 2V3C15 3.55228 14.5523 4 14 4H10C9.44772 4 9 3.55228 9 3V2Z" fill="#ff6d91"/>
                                <path d="M6 8V6C6 5.44772 6.44772 5 7 5H17C17.5523 5 18 5.44772 18 6V8C18 8.55228 17.5523 9 17 9H7C6.44772 9 6 8.55228 6 8Z" fill="#ff6d91"/>
                                <path d="M8 9H16L20 17C21 19 19.5 21 17 21H7C4.5 21 3 19 4 17L8 9Z" stroke="#ff6d91" stroke-width="2"/>
                                <path d="M19 3H21M20 2V4M4 3H6M5 2V4M16 5H18M17 4V6" stroke="#ff6d91" stroke-width="2" stroke-linecap="round"/>
                            </svg>`;
    private debugIcon = `<svg class="${this.debugCtrlIconKey}" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M10 2C9.44772 2 9 2.44772 9 3V4.07071C7.2845 4.55606 6 6.13616 6 8V10H4C3.44772 10 3 10.4477 3 11C3 11.5523 3.44772 12 4 12H6V14H4C3.44772 14 3 14.4477 3 15C3 15.5523 3.44772 16 4 16H6V18C6 19.8638 7.2845 21.4439 9 21.9293V23C9 23.5523 9.44772 24 10 24C10.5523 24 11 23.5523 11 23V22H13V23C13 23.5523 13.4477 24 14 24C14.5523 24 15 23.5523 15 23V21.9293C16.7155 21.4439 18 19.8638 18 18V16H20C20.5523 16 21 15.5523 21 15C21 14.4477 20.5523 14 20 14H18V12H20C20.5523 12 21 11.5523 21 11C21 10.4477 20.5523 10 20 10H18V8C18 6.13616 16.7155 4.55606 15 4.07071V3C15 2.44772 14.5523 2 14 2C13.4477 2 13 2.44772 13 3V4H11V3C11 2.44772 10.5523 2 10 2Z" fill="#ff6d91"/>
                            <circle cx="17" cy="17" r="5" stroke="#ff6d91" stroke-width="2"/>
                            <line x1="20.5" y1="20.5" x2="23" y2="23" stroke="#ff6d91" stroke-width="2"/>
                        </svg>`;
    private copyIcon = function (deviceId: string) {
        return `<svg 
            class="pulse-debug-ctrl-copy-icon"
            onclick="(()=>{
                try {
                    navigator.clipboard.writeText('${deviceId}');
                } catch (err) {
                    console.log('Failed to copy:', err);
                }
            })()" 
            width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="8" y="4" width="12" height="14" rx="2" stroke="#ff6d91" stroke-width="2"/>
            <path d="M6 8H5C3.89543 8 3 8.89543 3 10V18C3 19.1046 3.89543 20 5 20H13C14.1046 20 15 19.1046 15 18V17" stroke="#ff6d91" stroke-width="2"/>
        </svg>`;
    };

    constructor(private env: PulseEnvironment) {}

    registerControl({
        control,
        content,
        url,
        title = '',
        deviceId = '',
        callbackFn,
    }: {
        control: ControlName;
        content: string;
        url: string;
        title: string;
        deviceId?: string;
        callbackFn: () => void;
    }) {
        if (!this.registeredConrols.has(control)) {
            this.registeredConrols.set(
                control,
                this.displayControls({
                    controlName: control,
                    content,
                    url,
                    title,
                    deviceId,
                    callbackFn,
                }),
            );
        }
    }

    unregisterControl(control: ControlName) {
        const registredControl = this.registeredConrols.get(control);

        if (registredControl) {
            this.animateOut(registredControl);
            this.registeredConrols.delete(control);
        }
    }

    private displayControls({
        controlName,
        content,
        url,
        title,
        deviceId,
        callbackFn,
    }: DebugControlInfo): DebugControls {
        const control = this.createElements({ controlName, content, url, title, deviceId });

        control.button.addEventListener('click', () => {
            callbackFn();
        });

        this.addStyles(controlName);
        this.animateIn(control);

        return control;
    }

    private async animateIn(badge: DebugControls) {
        const doc = window.document;
        doc.body.appendChild(badge.fragment);

        await sleep(10);
        badge.container.classList.add('active');

        await sleep(500);
        badge.container.classList.add('animate');

        await sleep(1500);
        badge.container.classList.remove('animate');
    }

    private async animateOut(badge: DebugControls) {
        badge.container.classList.remove('active');
        await sleep(1000);
        this.env.win.location.reload();
    }

    private createElements({
        controlName,
        content,
        url,
        title,
        deviceId,
    }: {
        controlName: ControlName;
        content: string;
        url: string;
        title: string;
        deviceId?: string;
    }) {
        const doc = window.document;
        let icon: string;
        let exitText: string;

        switch (controlName) {
            case 'experiment':
                icon = this.experimentIcon;
                exitText = 'Exit preview';
                break;
            case 'debug':
                icon = this.debugIcon;
                exitText = 'Exit from debug mode';
                break;
        }

        const deviceIdElement = deviceId
            ? `<p class="pulse-debug-ctrl-${controlName}-device-id">${deviceId} ${this.copyIcon(
                  deviceId,
              )}</p>`
            : '';

        const html = `${icon}<div class="pulse-debug-ctrl-${controlName}-details">
                                <div class="pulse-debug-ctrl-${controlName}-title">
                                    <a href="${url}" title="${title}" target="_blank">${content}</a>
                                </div>
                            <div>
                            ${deviceIdElement}
                            <button>${exitText}</button>
                            </div>
                            </div>`;
        const container = doc.createElement('div');
        container.classList.add(`pulse-debug-ctrl-${controlName}-badge`);
        container.innerHTML = html;

        const fragment = doc.createDocumentFragment();
        fragment.appendChild(container);

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const exitBtn = fragment.querySelector<HTMLButtonElement>(
            `.pulse-debug-ctrl-${controlName}-details button`,
        )!;

        return { container, fragment, button: exitBtn };
    }

    private addStyles(controlName: ControlName) {
        const css = `
        .pulse-debug-ctrl-${controlName}-badge {
            display: flex;
            position: fixed;
            z-index: 9999999999;
            box-sizing: border-box;
            align-items: center;
            width: 310px;
            height: 60px;
            bottom: ${controlName === 'debug' ? '55vh' : '48vh'};
            right: 0;
            border: solid 1px #ff6d91;
            border-right: none;
            border-radius: 1000px;
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
            box-shadow: 0 2px 25px rgba(255, 0, 130, 0.1);
            color: #fff;
            text-align: center;
            font-family: sans-serif;
            font-size: 10px;
            background-color: #1b1b1b;
            -webkit-user-select: none;
            -ms-user-select: none;
            user-select: none;
            transition: transform 0.2s;
            transform: translateX(345px);
        }

        .pulse-debug-ctrl-${controlName}-badge .pulse-debug-ctrl-copy-icon{
            cursor: pointer;
        }

        .pulse-debug-ctrl-${controlName}-badge .${this.debugCtrlIconKey} {
            margin: 0 10px 0 10px;
        }

        .pulse-debug-ctrl-${controlName}-badge.active {
            transition-delay: 0.5s;
            transform: translateX(275px);
        }

        .pulse-debug-ctrl-${controlName}-badge.active:hover {
            transform: translateX(0);
        }

        .pulse-debug-ctrl-${controlName}-badge .pulse-debug-ctrl-${controlName}-details {
            width: 10px;
            flex-grow: 1111;
        }
        
        .pulse-debug-ctrl-${controlName}-badge .pulse-debug-ctrl-${controlName}-device-id {
            margin: 0px 0px 3px 0px;
        }

        .pulse-debug-ctrl-${controlName}-device-id {
            cursor: pointer;
        }

        .pulse-debug-ctrl-${controlName}-badge .pulse-debug-ctrl-${controlName}-title {
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            width: 100%;
            text-align: center;
            padding: 0;
            margin: 0 0 3px 0;
            display: block;
        }

        .pulse-debug-ctrl-${controlName}-badge .pulse-debug-ctrl-${controlName}-title a {
            color: #fff;
            text-decoration: none;
            padding: 0;
            margin: 0;
        }

        .pulse-debug-ctrl-${controlName}-badge .pulse-debug-ctrl-${controlName}-title a:hover {
            text-decoration: underline;
        }

        .pulse-debug-ctrl-${controlName}-badge button {
            padding: 1px 5px;
            border: solid 1px #ff6d91;
            border-radius: 3px;
            cursor: pointer;
            background: none;
            color: #ff6d91;
            font-size: 10px;
            transition: all 0.15s;
        }

        .pulse-debug-ctrl-${controlName}-badge button:hover {
            color: #fff;
            background-color: #bb1367;
            border: solid 1px #bb1367;
        }

        .pulse-debug-ctrl-${controlName}-badge:before,
        .pulse-debug-ctrl-${controlName}-badge:after {
            position: absolute;
            content: "";
            display: block;
            width: 140%;
            height: 100%;
            left: -20%;
            z-index: -1000;
            transition: all ease-in-out 0.5s;
            background-repeat: no-repeat;
        }

        .pulse-debug-ctrl-${controlName}-badge:before {
            display: none;
            top: -75%;
            background-image: radial-gradient(circle, #ff0081 20%, transparent 20%),
                            radial-gradient(circle, transparent 20%, #ff0081 20%, transparent 30%),
                            radial-gradient(circle, #ff0081 20%, transparent 20%),
                            radial-gradient(circle, #ff0081 20%, transparent 20%),
                            radial-gradient(circle, transparent 10%, #ff0081 15%, transparent 20%),
                            radial-gradient(circle, #ff0081 20%, transparent 20%),
                            radial-gradient(circle, #ff0081 20%, transparent 20%),
                            radial-gradient(circle, #ff0081 20%, transparent 20%),
                            radial-gradient(circle, #ff0081 20%, transparent 20%);
            background-size: 10% 10%, 20% 20%, 15% 15%, 20% 20%, 18% 18%, 10% 10%, 15% 15%, 10% 10%, 18% 18%;
        }

        .pulse-debug-ctrl-${controlName}-badge:after {
            display: none;
            bottom: -75%;
            background-image: radial-gradient(circle, #ff0081 20%, transparent 20%),
                            radial-gradient(circle, #ff0081 20%, transparent 20%),
                            radial-gradient(circle, transparent 10%, #ff0081 15%, transparent 20%),
                            radial-gradient(circle, #ff0081 20%, transparent 20%),
                            radial-gradient(circle, #ff0081 20%, transparent 20%),
                            radial-gradient(circle, #ff0081 20%, transparent 20%),
                            radial-gradient(circle, #ff0081 20%, transparent 20%);
            background-size: 15% 15%, 20% 20%, 18% 18%, 20% 20%, 15% 15%, 10% 10%, 20% 20%;
        }

        .pulse-debug-ctrl-${controlName}-badge.animate:before {
            display: block;
            animation: topBubbles ease-in-out 0.75s forwards;
        }

        .pulse-debug-ctrl-${controlName}-badge.animate:after {
            display: block;
            animation: bottomBubbles ease-in-out 0.75s forwards;
        }

        @keyframes topBubbles {
            0% {
                background-position: 5% 90%, 10% 90%, 10% 90%, 15% 90%, 25% 90%, 25% 90%, 40% 90%, 55% 90%, 70% 90%;
            }
            50% {
                background-position: 0 80%, 0% 20%, 10% 40%, 20% 0%, 30% 30%, 22% 50%, 50% 50%, 65% 20%, 90% 30%;
            }
            100% {
                background-position: 0 70%, 0% 10%, 10% 30%, 20% -10%, 30% 20%, 22% 40%, 50% 40%, 65% 10%, 90% 20%;
                background-size: 0 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%;
            }
        }

        @keyframes bottomBubbles {
            0% {
                background-position: 10% -10%, 30% 10%, 55% -10%, 70% -10%, 85% -10%, 70% -10%, 70% 0%;
            }
            50% {
                background-position: 0 80%, 20% 80%, 45% 60%, 60% 100%, 75% 70%, 95% 60%, 105% 0%;
            }
            100% {
                background-position: 0 90%, 20% 90%, 45% 70%, 60% 110%, 75% 80%, 95% 70%, 110% 10%;
                background-size: 0 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%, 0% 0%;
            }
        }
        `;
        const head = document.head || document.getElementsByTagName('head')[0];
        const style = document.createElement('style');
        style.appendChild(document.createTextNode(css));
        head.appendChild(style);
    }
}
